import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import './RagEvalArticle.css';

interface Source {
  id: string;
  name: string;
  type: string;
  url: string;
}

interface Metric {
  name: string;
  description: string;
  needsGroundTruth: boolean;
  sourceIds: string[];
  aliases?: string;
  crossRef?: string;
}

interface Framework {
  name: string;
  description: string;
  url: string;
  metrics: string[];
  sourceIds: string[];
}

const sources: Source[] = [
  { id: 'S1', name: 'RAGAS: Automated Evaluation of RAG (Es et al., 2023)', type: 'Academic paper', url: 'https://arxiv.org/abs/2309.15217' },
  { id: 'S2', name: 'RAGAS Documentation — Available Metrics', type: 'Framework docs', url: 'https://docs.ragas.io/en/stable/concepts/metrics/available_metrics/' },
  { id: 'S3', name: 'Evaluation of Retrieval-Augmented Generation: A Survey (Yu et al., 2024)', type: 'Academic survey', url: 'https://arxiv.org/abs/2405.07437' },
  { id: 'S4', name: 'Comprehensive Survey of RAG Evaluation and Benchmarks (2025)', type: 'Academic survey', url: 'https://arxiv.org/abs/2504.14891' },
  { id: 'S5', name: 'Evidently AI — RAG Evaluation Guide', type: 'Framework guide', url: 'https://www.evidentlyai.com/llm-guide/rag-evaluation' },
  { id: 'S6', name: 'Confident AI — RAG Evaluation Metrics', type: 'Framework guide', url: 'https://www.confident-ai.com/blog/rag-evaluation-metrics-answer-relevancy-faithfulness-and-more' },
  { id: 'S7', name: 'DeepEval — RAG Evaluation Guide', type: 'Framework guide', url: 'https://deepeval.com/guides/guides-rag-evaluation' },
  { id: 'S8', name: 'Pinecone — RAG Evaluation Guide', type: 'Practitioner', url: 'https://www.pinecone.io/learn/series/vector-databases-in-production-for-busy-engineers/rag-evaluation/' },
  { id: 'S9', name: 'Braintrust — Best RAG Evaluation Tools', type: 'Framework guide', url: 'https://www.braintrust.dev/articles/best-rag-evaluation-tools' },
  { id: 'S10', name: 'Databricks — LLM Auto-Eval Best Practices for RAG', type: 'Practitioner', url: 'https://www.databricks.com/blog/LLM-auto-eval-best-practices-RAG' },
  { id: 'S11', name: 'AWS — RAG Evaluation and LLM-as-a-Judge in Amazon Bedrock', type: 'Cloud provider', url: 'https://aws.amazon.com/blogs/aws/new-rag-evaluation-and-llm-as-a-judge-capabilities-in-amazon-bedrock/' },
  { id: 'S12', name: 'Meilisearch — RAG Evaluation: Metrics, Methodologies, Best Practices', type: 'Practitioner', url: 'https://www.meilisearch.com/blog/rag-evaluation' },
];

const retrievalMetrics: Metric[] = [
  {
    name: 'Precision@k',
    description: 'Fraction of the top-k retrieved documents that are actually relevant to the query. High precision means less noise in the context window.',
    needsGroundTruth: true,
    sourceIds: ['S1', 'S3', 'S4', 'S5', 'S6', 'S8', 'S12'],
  },
  {
    name: 'Recall@k',
    description: 'Fraction of all relevant documents that appear in the top-k results. High recall means the retriever is finding most of the useful information.',
    needsGroundTruth: true,
    sourceIds: ['S1', 'S3', 'S4', 'S5', 'S6', 'S8', 'S12'],
  },
  {
    name: 'Mean Reciprocal Rank (MRR)',
    description: 'Average of 1/rank of the first relevant document across queries. Measures how quickly a user would find a relevant result.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S4', 'S5', 'S8', 'S12'],
  },
  {
    name: 'Normalized Discounted Cumulative Gain (nDCG)',
    description: 'Measures ranking quality with graded relevance, applying logarithmic discount to lower-ranked results. The standard metric for search quality.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S4', 'S5', 'S8', 'S12'],
  },
  {
    name: 'Mean Average Precision (MAP)',
    description: 'Average of precision values computed at each relevant document position. Combines precision and recall into a single ranking metric.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S4', 'S8'],
  },
  {
    name: 'Hit Rate / Success@k',
    description: 'Binary measure: is at least one relevant document present in the top-k results? The simplest retrieval quality check.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S5', 'S8'],
  },
  {
    name: 'Context Precision',
    description: 'Signal-to-noise ratio of retrieved context — what proportion of the retrieved chunks are relevant? Introduced by RAGAS as a RAG-specific precision metric.',
    needsGroundTruth: true,
    aliases: 'RAGAS Context Precision',
    sourceIds: ['S1', 'S2', 'S5', 'S6', 'S7'],
  },
  {
    name: 'Context Recall',
    description: 'How much of the ground truth answer can be attributed to the retrieved context. Measures retrieval completeness for a specific question.',
    needsGroundTruth: true,
    aliases: 'RAGAS Context Recall',
    sourceIds: ['S1', 'S2', 'S5', 'S6', 'S7'],
  },
  {
    name: 'Context Entities Recall',
    description: 'Recall of named entities from the ground truth that appear in the retrieved context. Catches cases where relevant docs are retrieved but key entities are missing.',
    needsGroundTruth: true,
    sourceIds: ['S2', 'S7'],
  },
  {
    name: 'Context Relevance',
    description: 'LLM-judged relevance of each retrieved chunk to the query. Unlike precision@k, this uses semantic understanding rather than binary relevance labels.',
    needsGroundTruth: false,
    aliases: 'Contextual Relevancy',
    sourceIds: ['S5', 'S6', 'S7', 'S11'],
  },
];

const generationMetrics: Metric[] = [
  {
    name: 'Faithfulness / Groundedness',
    description: 'Is every claim in the response supported by the retrieved context? The most critical RAG generation metric — a faithful response may be incomplete, but an unfaithful one is actively misleading.',
    needsGroundTruth: false,
    aliases: 'Groundedness, context fidelity, attribution',
    crossRef: 'LLM Eval #10: Faithfulness / Groundedness',
    sourceIds: ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S10', 'S11', 'S12'],
  },
  {
    name: 'Answer Relevancy',
    description: 'Does the response actually address the user\'s question? A response can be faithful to the context but still miss the point of the query.',
    needsGroundTruth: false,
    aliases: 'Response Relevancy',
    sourceIds: ['S1', 'S2', 'S5', 'S6', 'S7', 'S8', 'S12'],
  },
  {
    name: 'Hallucination Rate',
    description: 'Proportion of claims in the response that are not supported by the retrieved context. The inverse of faithfulness — measures how much the generator "makes up."',
    needsGroundTruth: false,
    sourceIds: ['S3', 'S4', 'S5', 'S6', 'S8', 'S12'],
  },
  {
    name: 'Answer Correctness / Factual Correctness',
    description: 'Overall factual accuracy of the response compared to a known-correct ground truth answer. Combines faithfulness and completeness.',
    needsGroundTruth: true,
    aliases: 'Factual Correctness',
    crossRef: 'LLM Eval #9: Reference-Based Evaluation',
    sourceIds: ['S2', 'S5', 'S6', 'S7', 'S10'],
  },
  {
    name: 'Citation Accuracy / Coverage',
    description: 'Are claims in the response properly attributed to specific source documents? Measures whether the system can point users to evidence.',
    needsGroundTruth: false,
    aliases: 'Attribution accuracy, source citation',
    sourceIds: ['S3', 'S5', 'S8', 'S12'],
  },
  {
    name: 'Answer Completeness',
    description: 'Does the response cover all aspects of the query? A faithful, relevant response can still miss important parts of a multi-faceted question.',
    needsGroundTruth: true,
    aliases: 'Response completeness, coverage',
    crossRef: 'LLM Eval #43: Coverage / Completeness Checks',
    sourceIds: ['S5', 'S6', 'S7'],
  },
  {
    name: 'Semantic Similarity to Ground Truth',
    description: 'Embedding-based similarity between the generated response and the expected answer. Softer than exact match — captures meaning even with different wording.',
    needsGroundTruth: true,
    crossRef: 'LLM Eval #17: Semantic Similarity',
    sourceIds: ['S2', 'S3', 'S6', 'S10'],
  },
];

const diagnosticMetrics: Metric[] = [
  {
    name: 'Noise Sensitivity',
    description: 'How well does the system perform when irrelevant documents are mixed into the retrieved context? Good RAG systems should ignore noise, not incorporate it.',
    needsGroundTruth: true,
    aliases: 'Noise robustness',
    sourceIds: ['S2', 'S3', 'S4', 'S7'],
  },
  {
    name: 'Negative Rejection',
    description: 'When the retrieved context doesn\'t contain the answer, does the system say "I don\'t know" instead of hallucinating? Critical for trust in production systems.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S4', 'S12'],
  },
  {
    name: 'Counterfactual Robustness',
    description: 'Can the system detect and reject false information present in retrieved documents? Tests whether the generator blindly trusts all retrieved content.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S4'],
  },
  {
    name: 'Misleading Rate',
    description: 'How often does misleading context (partially relevant but ultimately wrong) cause the system to produce incorrect answers?',
    needsGroundTruth: true,
    aliases: 'Mistake reappearance rate',
    sourceIds: ['S3', 'S4'],
  },
  {
    name: 'Information Integration',
    description: 'Can the system synthesize information from multiple retrieved chunks to form a coherent answer? Tests multi-hop reasoning over retrieved context.',
    needsGroundTruth: true,
    sourceIds: ['S3', 'S4'],
  },
  {
    name: 'Latency (Retrieval + Generation)',
    description: 'End-to-end response time including embedding the query, vector search, and LLM generation. RAG adds retrieval latency on top of generation latency.',
    needsGroundTruth: false,
    crossRef: 'LLM Eval #37: Latency / Cost Measurement',
    sourceIds: ['S5', 'S8', 'S9', 'S12'],
  },
];

const frameworks: Framework[] = [
  {
    name: 'RAGAS',
    description: 'The de facto standard for RAG evaluation. Open-source framework with reference-free metrics, synthetic test data generation, and LLM-as-a-judge scoring.',
    url: 'https://docs.ragas.io/',
    metrics: ['Context Precision', 'Context Recall', 'Faithfulness', 'Answer Relevancy', 'Noise Sensitivity', 'Context Entities Recall'],
    sourceIds: ['S1', 'S2'],
  },
  {
    name: 'DeepEval',
    description: 'Open-source evaluation framework by Confident AI with RAG-specific metrics, pytest integration, and CI/CD support for continuous evaluation.',
    url: 'https://deepeval.com/',
    metrics: ['Faithfulness', 'Answer Relevancy', 'Contextual Precision', 'Contextual Recall', 'Hallucination'],
    sourceIds: ['S6', 'S7'],
  },
  {
    name: 'ARES',
    description: 'Adversarial evaluation framework that stress-tests retrieval systems with synthetic perturbations. Useful for measuring robustness in dynamic environments.',
    url: 'https://github.com/stanford-futuredata/ARES',
    metrics: ['Context Relevance', 'Answer Faithfulness', 'Answer Relevance'],
    sourceIds: ['S9'],
  },
  {
    name: 'TruLens',
    description: 'Observability and evaluation framework for LLM apps. Tracks the "RAG Triad" — answer relevance, context relevance, and groundedness — with logging and dashboards.',
    url: 'https://www.trulens.org/',
    metrics: ['Answer Relevance', 'Context Relevance', 'Groundedness'],
    sourceIds: ['S9', 'S12'],
  },
  {
    name: 'Amazon Bedrock Evaluation',
    description: 'Managed RAG evaluation with built-in LLM-as-a-judge, supporting faithfulness, relevance, and completeness scoring without custom infrastructure.',
    url: 'https://aws.amazon.com/bedrock/',
    metrics: ['Faithfulness', 'Answer Relevance', 'Completeness', 'Harmfulness'],
    sourceIds: ['S11'],
  },
];

const llmEvalOverlap = [
  { ragMetric: 'Faithfulness / Groundedness', llmEvalRank: 10, llmEvalName: 'Faithfulness / Groundedness', relationship: 'Same concept, but RAG version specifically checks against retrieved context rather than general knowledge.' },
  { ragMetric: 'Answer Correctness', llmEvalRank: 9, llmEvalName: 'Reference-Based Evaluation', relationship: 'Both compare output to a gold-standard answer. RAG version adds retrieval context as a variable.' },
  { ragMetric: 'Semantic Similarity', llmEvalRank: 17, llmEvalName: 'Semantic Similarity', relationship: 'Identical technique applied to RAG output comparison.' },
  { ragMetric: 'Context Precision / Recall', llmEvalRank: 23, llmEvalName: 'Context Precision / Context Recall (RAG-specific)', relationship: 'Already captured in the LLM eval list as RAG-specific metrics.' },
  { ragMetric: 'Answer Completeness', llmEvalRank: 43, llmEvalName: 'Coverage / Completeness Checks', relationship: 'Same concept — checking if all required information is present in the response.' },
  { ragMetric: 'Latency', llmEvalRank: 37, llmEvalName: 'Latency / Cost Measurement', relationship: 'RAG adds retrieval latency on top of generation latency.' },
  { ragMetric: 'LLM-as-a-Judge (for scoring)', llmEvalRank: 1, llmEvalName: 'LLM-as-a-Judge', relationship: 'RAG frameworks (RAGAS, DeepEval, TruLens) all use LLM-as-a-Judge as the primary scoring mechanism.' },
];

const MetricCard: React.FC<{ metric: Metric; index: number }> = ({ metric, index }) => (
  <div className="rag-metric-card">
    <div className="rag-metric-header">
      <span className="rag-metric-rank">#{index + 1}</span>
      <h3 className="rag-metric-name">{metric.name}</h3>
      <span className={`rag-gt-badge ${metric.needsGroundTruth ? 'gt-required' : 'gt-free'}`}>
        {metric.needsGroundTruth ? 'Needs Ground Truth' : 'Reference-Free'}
      </span>
    </div>
    <p className="rag-metric-description">{metric.description}</p>
    {metric.aliases && (
      <p className="rag-metric-aliases">
        <span className="aliases-label">Also known as:</span> {metric.aliases}
      </p>
    )}
    {metric.crossRef && (
      <p className="rag-metric-crossref">
        <Link to="/wiki/eval-methods" className="crossref-link">
          {metric.crossRef}
        </Link>
      </p>
    )}
    <div className="rag-source-pills">
      {metric.sourceIds.map((id) => (
        <span key={id} className="rag-source-pill">{id}</span>
      ))}
    </div>
  </div>
);

const RagEvalArticle: React.FC = () => {
  const [sourcesOpen, setSourcesOpen] = useState(false);

  return (
    <>
      <div className="rag-article-page">
        <div className="rag-article-container">
          <Link to="/wiki" className="back-link">← Back to Wiki</Link>

          <header className="rag-article-header">
            <h1>RAG Evaluation Methods</h1>
            <p className="rag-article-subtitle">Metrics, Diagnostics & Frameworks</p>
            <p className="rag-article-description">
              A structured guide to evaluating Retrieval-Augmented Generation systems.
              RAG evaluation is a superset of LLM evaluation — it uses the same generation-quality
              methods but adds an entire retrieval evaluation layer and pipeline-level diagnostics.
            </p>
          </header>

          <section className="rag-pipeline-overview">
            <h2>The Three-Layer Problem</h2>
            <p className="rag-pipeline-intro">
              A bad RAG answer can fail at any stage. The retriever might miss relevant docs,
              the generator might hallucinate despite good docs, or the pipeline might break
              under edge cases. RAG evaluation must diagnose <em>where</em> things went wrong.
            </p>
            <div className="rag-pipeline-cards">
              <div className="rag-pipeline-card">
                <div className="rag-pipeline-icon">1</div>
                <h3>Retrieval</h3>
                <p>Did we find the right documents?</p>
                <span className="rag-pipeline-count">{retrievalMetrics.length} metrics</span>
              </div>
              <div className="rag-pipeline-arrow">→</div>
              <div className="rag-pipeline-card">
                <div className="rag-pipeline-icon">2</div>
                <h3>Generation</h3>
                <p>Did we use them correctly?</p>
                <span className="rag-pipeline-count">{generationMetrics.length} metrics</span>
              </div>
              <div className="rag-pipeline-arrow">→</div>
              <div className="rag-pipeline-card">
                <div className="rag-pipeline-icon">3</div>
                <h3>Diagnostics</h3>
                <p>Does the pipeline handle edge cases?</p>
                <span className="rag-pipeline-count">{diagnosticMetrics.length} metrics</span>
              </div>
            </div>
          </section>

          <section className="rag-sources-section">
            <button
              className="rag-sources-toggle"
              onClick={() => setSourcesOpen(!sourcesOpen)}
              aria-expanded={sourcesOpen}
            >
              <span className="rag-sources-toggle-icon">{sourcesOpen ? '−' : '+'}</span>
              <span>Sources Key ({sources.length} sources)</span>
            </button>
            {sourcesOpen && (
              <div className="rag-sources-table-wrapper">
                <table className="rag-sources-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Source</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((source) => (
                      <tr key={source.id}>
                        <td className="rag-source-id">{source.id}</td>
                        <td>
                          <a href={source.url} target="_blank" rel="noopener noreferrer">
                            {source.name}
                          </a>
                        </td>
                        <td className="rag-source-type">{source.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rag-layer-section">
            <h2 className="rag-layer-heading">
              <span className="rag-layer-label layer-retrieval">Retrieval</span>
              Retrieval Quality Metrics
            </h2>
            <p className="rag-layer-description">
              Borrowed from information retrieval (IR) research. These measure whether the right
              documents were found and ranked well — before the LLM ever sees them.
            </p>
            <div className="rag-metrics-list">
              {retrievalMetrics.map((metric, i) => (
                <MetricCard key={metric.name} metric={metric} index={i} />
              ))}
            </div>
          </section>

          <section className="rag-layer-section">
            <h2 className="rag-layer-heading">
              <span className="rag-layer-label layer-generation">Generation</span>
              Generation Quality Metrics
            </h2>
            <p className="rag-layer-description">
              These measure the LLM's output quality <em>given</em> the retrieved context. Faithfulness
              is king here — an unfaithful response is worse than an incomplete one.
            </p>
            <div className="rag-metrics-list">
              {generationMetrics.map((metric, i) => (
                <MetricCard key={metric.name} metric={metric} index={retrievalMetrics.length + i} />
              ))}
            </div>
          </section>

          <section className="rag-layer-section">
            <h2 className="rag-layer-heading">
              <span className="rag-layer-label layer-diagnostic">Diagnostic</span>
              Pipeline Diagnostic Metrics
            </h2>
            <p className="rag-layer-description">
              Edge-case and robustness metrics that test the full pipeline under adversarial
              or degraded conditions. These reveal failure modes you won't catch with standard metrics.
            </p>
            <div className="rag-metrics-list">
              {diagnosticMetrics.map((metric, i) => (
                <MetricCard key={metric.name} metric={metric} index={retrievalMetrics.length + generationMetrics.length + i} />
              ))}
            </div>
          </section>

          <section className="rag-frameworks-section">
            <h2 className="rag-layer-heading">
              <span className="rag-layer-label layer-framework">Tools</span>
              Evaluation Frameworks
            </h2>
            <div className="rag-frameworks-list">
              {frameworks.map((fw) => (
                <div key={fw.name} className="rag-framework-card">
                  <div className="rag-framework-header">
                    <h3 className="rag-framework-name">
                      <a href={fw.url} target="_blank" rel="noopener noreferrer">{fw.name}</a>
                    </h3>
                    <div className="rag-source-pills">
                      {fw.sourceIds.map((id) => (
                        <span key={id} className="rag-source-pill">{id}</span>
                      ))}
                    </div>
                  </div>
                  <p className="rag-framework-description">{fw.description}</p>
                  <div className="rag-framework-metrics">
                    {fw.metrics.map((m) => (
                      <span key={m} className="rag-framework-metric-tag">{m}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rag-crossref-section">
            <h2 className="rag-layer-heading">
              <span className="rag-layer-label layer-crossref">Overlap</span>
              RAG Eval vs LLM Eval
            </h2>
            <p className="rag-layer-description">
              RAG evaluation reuses many methods from general LLM evaluation. Here's how they map
              to the <Link to="/wiki/eval-methods" className="crossref-link">45 LLM Evaluation Methods</Link> article.
            </p>
            <div className="rag-crossref-table-wrapper">
              <table className="rag-crossref-table">
                <thead>
                  <tr>
                    <th>RAG Metric</th>
                    <th>LLM Eval Method</th>
                    <th>Relationship</th>
                  </tr>
                </thead>
                <tbody>
                  {llmEvalOverlap.map((row) => (
                    <tr key={row.ragMetric}>
                      <td className="rag-crossref-rag">{row.ragMetric}</td>
                      <td className="rag-crossref-llm">#{row.llmEvalRank} {row.llmEvalName}</td>
                      <td>{row.relationship}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RagEvalArticle;
